import { type CHCameraId } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { toast } from '@solinkcorp/solink-components';
import {
  type AiNotification,
  type ScheduledActivityEntity,
  ScheduledActivityRuleType,
} from 'clients/CallhomeApiTypes';
import { type AI_INSPECTION_GROUP_OPTIONS } from 'components/ScheduledActivityForm/ScheduledActivityForm';
import { type NewActivityType } from 'components/ScheduledActivityForm/useScheduledEventForm';
import moment from 'moment-timezone';
import React from 'react';
import { useEventReports } from 'src/hooks/useEventReports';
import useLocale from 'src/hooks/useLocale';
import type { ValueOf } from 'type-fest';
import LocaleUtil from 'utils/LocaleUtil';
import {
  CRON_ALL_VALUES,
  getRecurrence,
  getRepeatOptions,
  getRepeatOptionValue,
  parseCron,
} from 'utils/RecurrenceUtil';
import type ReportUtil from 'utils/ReportUtil';
import { type VideoDiscoverySite } from 'utils/types';
import {
  DOES_NOT_REPEAT,
  REPEAT_EVERY_15MIN,
  REPEAT_EVERY_30MIN,
  REPEAT_EVERY_4H,
  REPEAT_EVERY_6H,
  REPEAT_HOURLY,
} from 'vd-constants';
import * as yup from 'yup';
import { ValidationError } from 'yup';
import { useSpotChecks } from './useSpotChecks';

interface UseSpotCheckFormProps {
  spotCheck?: ScheduledActivityEntity;
  eventSpotCheck?: AiNotification;
  selectedCameraIds: CHCameraId[];
  timezoneId: string;
  selectedLocations: VideoDiscoverySite[];
}

type WeeklyScheduleOptions =
  | 'ai-chat-schedule-off'
  | 'ai-chat-schedule-in-schedule'
  | 'ai-chat-schedule-out-schedule';

export const AI_CHAT_WEEKLY_SCHEDULE_OFF = 'ai-chat-schedule-off';
export const AI_CHAT_WEEKLY_SCHEDULE_IN_SCHEDULE = 'ai-chat-schedule-in-schedule';
export const AI_CHAT_WEEKLY_SCHEDULE_OUT_SCHEDULE = 'ai-chat-schedule-out-schedule';

export const AI_CHAT_WEEKLY_SCHEDULE_OPTIONS = {
  [AI_CHAT_WEEKLY_SCHEDULE_IN_SCHEDULE]: {
    text: 'duringStoreOperatingHours',
    metadataFields: {
      weeklyScheduleId: 'aiInferredOffHours',
      inSchedulePeriods: false,
    },
  },
  [AI_CHAT_WEEKLY_SCHEDULE_OUT_SCHEDULE]: {
    text: 'duringStoreOffHours',
    metadataFields: {
      weeklyScheduleId: 'aiInferredOffHours',
      inSchedulePeriods: true,
    },
  },
  [AI_CHAT_WEEKLY_SCHEDULE_OFF]: {
    text: 'noWeeklySchedule',
    metadataFields: {
      weeklyScheduleId: null,
      inSchedulePeriods: true,
    },
  },
} as const;

export const AI_INSPECTION_END_TYPE = {
  date: 'DATE',
  occurrence: 'OCCURRENCE',
  never: 'NEVER',
} as const;

export type AIInspectionDeviceType = ValueOf<typeof AI_INSPECTION_GROUP_OPTIONS>;
export type AiInspectionEndType = ValueOf<typeof AI_INSPECTION_END_TYPE>;

const INITIAL_AI_CHAT_REOCCURRING_COUNT = 12;

export const TIME_TARGETS = { START: 'START', END: 'END' };
export type AiTimeTargetType = ValueOf<typeof TIME_TARGETS>;
export const MIN_TIMESTAMP_OFFSET = -1000;
export const MAX_TIMESTAMP_OFFSET = 1000;

export interface SpotCheckState {
  id: string;
  name: string;
  description: string;
}

export const MAX_STATES = 3;
export const MIN_STATES = 1;
export const MAX_STATE_NAME_LENGTH = 45;
export const MAX_STATE_DESCRIPTION_LENGTH = 200;
const INITIAL_AI_CHAT_ANSWER_STATES = [{ name: '', description: '' }];

export default function useSpotCheckForm(props: UseSpotCheckFormProps) {
  const { spotCheck, eventSpotCheck, selectedCameraIds, timezoneId, selectedLocations } = props;
  const user = useUserSession();
  const customerId = user.profile?.customerId;
  const locale = useLocale();
  const [title, setTitle] = React.useState('');
  const [aiChatPrompt, setAiChatPrompt] = React.useState('');
  const [aiChatAnswerFormat, setAiChatAnswerFormat] = React.useState('');
  const [startTime, setStartTime] = React.useState(() => {
    const oneHourFromNow = moment().tz(timezoneId).add(1, 'hour').valueOf();
    return oneHourFromNow;
  });

  const [endTime, setEndTime] = React.useState(() => {
    const oneYearFromNow = moment().tz(timezoneId).add(1, 'year').valueOf();
    return oneYearFromNow;
  });

  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = React.useState<number[]>([
    0, 1, 2, 3, 4, 5, 6,
  ]);
  const [recurrence, setRecurrence] = React.useState(DOES_NOT_REPEAT);
  const [reoccurringCount, setReoccurringCount] = React.useState(INITIAL_AI_CHAT_REOCCURRING_COUNT);
  const [weeklyScheduleOption, setWeeklyScheduleOption] = React.useState<WeeklyScheduleOptions>(
    AI_CHAT_WEEKLY_SCHEDULE_IN_SCHEDULE,
  );
  const [selectedEndType, setSelectedEndType] = React.useState<AiInspectionEndType>(
    AI_INSPECTION_END_TYPE.occurrence,
  );
  const [selectedEventReport, setSelectedEventReport] = React.useState<ReportUtil | undefined>(
    undefined,
  );
  const [eventTimestampOffset, setEventTimestampOffset] = React.useState<number>(0);
  const [eventTimeTarget, setEventTimeTarget] = React.useState<AiTimeTargetType>(
    TIME_TARGETS.START,
  );
  const [aiChatAnswerStates, setAiChatAnswerStates] = React.useState<SpotCheckState[]>(() => {
    const now = Date.now();
    return INITIAL_AI_CHAT_ANSWER_STATES.map((state, index) => ({
      id: String(now + index),
      ...state,
    }));
  });

  const spotChecks = useSpotChecks();

  const eventReports = useEventReports();

  const recurrenceOptions = React.useMemo(
    () => getRepeatOptions(startTime, locale, timezoneId, true, true),
    [startTime, locale, timezoneId],
  );

  const initializeSelectedEndType = React.useCallback((spotCheck: ScheduledActivityEntity) => {
    if (spotCheck.endsAt === -1 && spotCheck.reoccurringCount === -1) {
      setSelectedEndType(AI_INSPECTION_END_TYPE.never);
    } else if (spotCheck.endsAt !== -1) {
      setSelectedEndType(AI_INSPECTION_END_TYPE.date);
    } else {
      setSelectedEndType(AI_INSPECTION_END_TYPE.occurrence);
    }
  }, []);

  const initializeWeeklyScheduleOption = React.useCallback((spotCheck: ScheduledActivityEntity) => {
    const { weeklyScheduleId, inSchedulePeriods } = spotCheck.metadata || {};
    if (weeklyScheduleId === null) {
      setWeeklyScheduleOption(AI_CHAT_WEEKLY_SCHEDULE_OFF);
    } else if (weeklyScheduleId === 'aiInferredOffHours' && inSchedulePeriods) {
      setWeeklyScheduleOption(AI_CHAT_WEEKLY_SCHEDULE_OUT_SCHEDULE);
    } else if (weeklyScheduleId === 'aiInferredOffHours' && !inSchedulePeriods) {
      setWeeklyScheduleOption(AI_CHAT_WEEKLY_SCHEDULE_IN_SCHEDULE);
    }
  }, []);

  const initializeDaysOfWeek = React.useCallback((spotCheck: ScheduledActivityEntity) => {
    const cron = parseCron(spotCheck.rule);

    const dayOfWeek = cron.day;
    const daysOfWeek =
      dayOfWeek === CRON_ALL_VALUES ? [] : dayOfWeek.split(',').map((num) => parseInt(num));
    setSelectedDaysOfWeek(daysOfWeek);
  }, []);

  const initializeFormState = React.useCallback(
    (spotCheck: ScheduledActivityEntity) => {
      setAiChatPrompt(spotCheck.metadata?.aiChatPrompt ?? '');
      setAiChatAnswerFormat(spotCheck.metadata?.aiChatAnswerFormat ?? '');
      setTitle(spotCheck.title ?? '');
      setStartTime(spotCheck.startsAt ?? moment().tz(timezoneId).add(1, 'hour').valueOf());

      if (spotCheck.endsAt != null && spotCheck.endsAt > 0) {
        setEndTime(spotCheck.endsAt);
      }

      if (spotCheck.reoccurringCount !== undefined && spotCheck.rule !== undefined) {
        setRecurrence(getRepeatOptionValue(spotCheck.rule, spotCheck.reoccurringCount));
      }

      if (spotCheck.reoccurringCount > 0) {
        setReoccurringCount(spotCheck.reoccurringCount);
      }

      if (spotCheck.rule) {
        initializeDaysOfWeek(spotCheck);
      }

      const aiChatAnswerStates =
        spotCheck.metadata?.aiChatAnswerStates &&
        spotCheck.metadata.aiChatAnswerStates.length >= MIN_STATES
          ? spotCheck.metadata?.aiChatAnswerStates
          : INITIAL_AI_CHAT_ANSWER_STATES;
      const baseTimestamp = Date.now();
      const templateStates = aiChatAnswerStates.map((state, index) => ({
        id: String(baseTimestamp + index),
        name: state.name || '',
        description: state.description || '',
      }));
      setAiChatAnswerStates(templateStates);

      initializeSelectedEndType(spotCheck);
      initializeWeeklyScheduleOption(spotCheck);
    },
    [initializeSelectedEndType, initializeDaysOfWeek, initializeWeeklyScheduleOption, timezoneId],
  );

  const eventSpotCheckReport = React.useMemo(() => {
    return eventReports?.find(
      (report) => report.getTemplateId() === eventSpotCheck?.reportTemplateId,
    );
  }, [eventReports, eventSpotCheck]);

  const initializeFormStateEventSpotCheck = React.useCallback(
    (eventSpotCheck: AiNotification) => {
      setAiChatPrompt(eventSpotCheck?.aiChatPrompt ?? '');
      setAiChatAnswerFormat(eventSpotCheck?.aiChatAnswerFormat ?? '');
      setTitle(
        eventSpotCheck?.metadata?.title ??
          (eventSpotCheck?.id ? locale.scheduledAIChat.eventSpotCheck : ''),
      );
      setEventTimeTarget(eventSpotCheck?.aiChatTimeTarget ?? TIME_TARGETS.START);
      setEventTimestampOffset(
        eventSpotCheck?.aiChatTimestampOffset ? eventSpotCheck.aiChatTimestampOffset / 1000 : 0,
      );

      if (eventSpotCheck?.reportTemplateId && eventSpotCheckReport) {
        setSelectedEventReport(eventSpotCheckReport);
      } else {
        setSelectedEventReport(undefined);
      }

      const aiChatAnswerStates =
        eventSpotCheck?.metadata?.aiChatAnswerStates &&
        eventSpotCheck.metadata.aiChatAnswerStates.length >= MIN_STATES
          ? eventSpotCheck.metadata.aiChatAnswerStates
          : INITIAL_AI_CHAT_ANSWER_STATES;
      const baseTimestamp = Date.now();
      const templateStates = aiChatAnswerStates.map((state, index) => ({
        id: String(baseTimestamp + index),
        name: state.name || '',
        description: state.description || '',
      }));
      setAiChatAnswerStates(templateStates);
    },
    [eventSpotCheckReport, locale],
  );

  React.useEffect(() => {
    if (spotCheck) {
      initializeFormState(spotCheck);
    }
  }, [spotCheck, initializeFormState]);

  React.useEffect(() => {
    if (eventSpotCheck) {
      initializeFormStateEventSpotCheck(eventSpotCheck);
    }
  }, [eventSpotCheck, initializeFormStateEventSpotCheck]);

  const getEndsAtAndReoccurringCount = React.useCallback(() => {
    if (recurrence === DOES_NOT_REPEAT) {
      return { endsAt: -1, reoccurringCount: 1 };
    }
    switch (selectedEndType) {
      case AI_INSPECTION_END_TYPE.date:
        return { endsAt: endTime.valueOf(), reoccurringCount: -1 };
      case AI_INSPECTION_END_TYPE.occurrence:
        return { endsAt: -1, reoccurringCount };
      case AI_INSPECTION_END_TYPE.never:
      default:
        return { endsAt: -1, reoccurringCount: -1 };
    }
  }, [recurrence, selectedEndType, endTime, reoccurringCount]);

  const getRule = React.useCallback(() => {
    const { cron } = getRecurrence(startTime, recurrence, timezoneId, selectedDaysOfWeek);
    return cron;
  }, [startTime, recurrence, timezoneId, selectedDaysOfWeek]);

  const getWeeklyScheduleIdAndInSchedulePeriods = React.useCallback(() => {
    if (
      recurrence === REPEAT_EVERY_15MIN ||
      recurrence === REPEAT_EVERY_30MIN ||
      recurrence === REPEAT_HOURLY ||
      recurrence === REPEAT_EVERY_6H ||
      recurrence === REPEAT_EVERY_4H
    ) {
      return {
        weeklyScheduleId:
          AI_CHAT_WEEKLY_SCHEDULE_OPTIONS[weeklyScheduleOption].metadataFields.weeklyScheduleId,
        inSchedulePeriods:
          AI_CHAT_WEEKLY_SCHEDULE_OPTIONS[weeklyScheduleOption].metadataFields.inSchedulePeriods,
      };
    }
    return { weeklyScheduleId: undefined, inSchedulePeriods: undefined };
  }, [recurrence, weeklyScheduleOption]);

  const isMultiLocation = selectedLocations.length > 1;

  const newActivity: NewActivityType = React.useMemo(() => {
    const { endsAt, reoccurringCount: newReoccurringCount } = getEndsAtAndReoccurringCount();
    const { weeklyScheduleId, inSchedulePeriods } = getWeeklyScheduleIdAndInSchedulePeriods();

    return {
      deviceId: undefined,
      title,
      reason: 'scheduled-ai-chat',
      cameraIds: selectedCameraIds,
      durationSecs: 0,
      startsAt: startTime.valueOf(),
      endsAt,
      ruleType: ScheduledActivityRuleType.CRON,
      rule: getRule(),
      reoccurringCount: newReoccurringCount,
      isAlarmsEnabled: false,
      metadata: {
        aiChatAnswerFormat: '',
        aiChatPrompt,
        aiChatAnswerStates: aiChatAnswerStates.map(({ name, description }) => ({
          name,
          description,
        })),
        weeklyScheduleId,
        inSchedulePeriods,
      },
    };
  }, [
    title,
    aiChatPrompt,
    aiChatAnswerStates,
    startTime,
    getRule,
    getEndsAtAndReoccurringCount,
    getWeeklyScheduleIdAndInSchedulePeriods,
    selectedCameraIds,
  ]);

  const createSpotCheck = React.useCallback(async () => {
    try {
      const response = await spotChecks.createScheduledActivity({ ...newActivity, customerId });
      toast.success(locale.scheduledAIChat.spotCheckCreated);
      return response;
    } catch (error) {
      toast.error(locale.scheduledAIChat.failedToCreateSpotCheck);
      throw error;
    }
  }, [
    newActivity,
    spotChecks,
    locale.scheduledAIChat.failedToCreateSpotCheck,
    locale.scheduledAIChat.spotCheckCreated,
    customerId,
  ]);

  const editSpotCheck = React.useCallback(async () => {
    try {
      if (
        spotCheck?.siteId &&
        (isMultiLocation || selectedLocations[0]?.id !== spotCheck?.siteId)
      ) {
        throw new Error(
          'Cannot edit: Spot check is assigned to a specific device, or the selected location does not match the device ID.',
        );
      }
      const response = await spotChecks.updateScheduledActivity(spotCheck?.id ?? '', newActivity);
      toast.success(locale.scheduledAIChat.spotCheckUpdated);
      return response;
    } catch (error) {
      toast.error(locale.scheduledAIChat.failedToUpdateSpotCheck);
      throw error;
    }
  }, [
    isMultiLocation,
    selectedLocations,
    newActivity,
    spotCheck,
    spotChecks,
    locale.scheduledAIChat.failedToUpdateSpotCheck,
    locale.scheduledAIChat.spotCheckUpdated,
  ]);

  const fiveMinutesFromNow = React.useMemo(
    () => moment().tz(timezoneId).add(5, 'minutes').valueOf(),
    [timezoneId],
  );

  const aiChatAnswerStateSchema = React.useMemo(
    () =>
      yup
        .array()
        .min(MIN_STATES, locale.scheduledAIChat.mustHaveOneState)
        .max(
          MAX_STATES,
          LocaleUtil.getLocalizedStr({
            string: locale.scheduledAIChat.noMoreThanMaxStates,
            replacements: { maxStates: MAX_STATES },
          }),
        )
        .of(
          yup.object({
            id: yup.string().required(),
            name: yup
              .string()
              .required(locale.scheduledAIChat.nameIsRequired)
              .test(
                'not-empty',
                locale.scheduledAIChat.nameIsRequired,
                (value) => value?.trim() !== '',
              )
              .max(MAX_STATE_NAME_LENGTH, (obj) =>
                LocaleUtil.getLocalizedStr({
                  string: locale.scheduledAIChat.nameMustNotExceedMaxLength,
                  replacements: {
                    maxLength: MAX_STATE_NAME_LENGTH,
                    currentLength: obj.value?.length,
                  },
                }),
              ),
            description: yup
              .string()
              .required(locale.scheduledAIChat.descriptionIsRequired)
              .test(
                'not-empty',
                locale.scheduledAIChat.descriptionIsRequired,
                (value) => value?.trim() !== '',
              )
              .max(MAX_STATE_DESCRIPTION_LENGTH, (obj) =>
                LocaleUtil.getLocalizedStr({
                  string: locale.scheduledAIChat.descriptionMustNotExceedMaxLength,
                  replacements: {
                    maxLength: MAX_STATE_DESCRIPTION_LENGTH,
                    currentLength: obj.value?.length,
                  },
                }),
              ),
          }),
        ),
    [locale],
  );

  const createSpotCheckSchema = (
    locale: LocaleDefinition,
    aiChatAnswerStateSchema: yup.AnySchema,
    minStartTime?: number,
  ) =>
    yup.object({
      title: yup.string().min(1, locale.scheduledAIChat.titleIsRequired),
      aiChatPrompt: yup.string().min(1, locale.scheduledAIChat.promptIsRequired),
      selectedCameraIds: yup
        .array()
        .of(yup.string())
        .min(1, locale.scheduledAIChat.mustSelectOneCamera),
      startTime:
        minStartTime == null
          ? yup.number()
          : yup.number().min(minStartTime, locale.alarms.atLeastFiveMinutesFromNow),
      aiChatAnswerStates: aiChatAnswerStateSchema,
    });

  const editSpotCheckSchema = React.useMemo(
    () => createSpotCheckSchema(locale, aiChatAnswerStateSchema),
    [locale, aiChatAnswerStateSchema],
  );
  const spotCheckSchema = React.useMemo(
    () => createSpotCheckSchema(locale, aiChatAnswerStateSchema, fiveMinutesFromNow),
    [locale, fiveMinutesFromNow, aiChatAnswerStateSchema],
  );

  const livePreviewSchema = React.useMemo(
    () =>
      yup.object({
        aiChatPrompt: yup.string().min(1, locale.scheduledAIChat.promptIsRequired),
        selectedCameraIds: yup
          .array()
          .of(yup.string())
          .min(1, locale.scheduledAIChat.mustSelectOneCamera),
        aiChatAnswerStates: aiChatAnswerStateSchema,
      }),
    [locale, aiChatAnswerStateSchema],
  );

  const eventTimOffsetText = LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.eventTimeOffsetMustBeInRange,
    replacements: { minValue: MIN_TIMESTAMP_OFFSET, maxValue: MAX_TIMESTAMP_OFFSET },
  });

  const eventLivePreviewSchema = React.useMemo(
    () =>
      yup.object({
        title: yup.string().min(1, locale.scheduledAIChat.titleIsRequired),
        aiChatPrompt: yup.string().min(1, locale.scheduledAIChat.promptIsRequired),
        selectedEventReport: yup
          .object({ templateId: yup.string().required() })
          .required(locale.scheduledAIChat.mustSelectOneReport),
        eventTimeOffset: yup
          .number()
          .min(MIN_TIMESTAMP_OFFSET, eventTimOffsetText)
          .max(MAX_TIMESTAMP_OFFSET, eventTimOffsetText),
        aiChatAnswerStates: aiChatAnswerStateSchema,
      }),
    [locale, eventTimOffsetText, aiChatAnswerStateSchema],
  );

  const spotCheckId = spotCheck?.id;
  const spotCheckValidationError: ValidationError | undefined = React.useMemo(() => {
    try {
      const isUpdate = spotCheckId !== undefined;
      const schema = isUpdate ? editSpotCheckSchema : spotCheckSchema;
      schema.validateSync(
        {
          title,
          aiChatPrompt,
          selectedCameraIds,
          startTime,
          aiChatAnswerStates,
        },
        { abortEarly: false },
      );
    } catch (error) {
      if (error instanceof ValidationError) {
        return error;
      }
      return undefined;
    }
  }, [
    spotCheckId,
    title,
    aiChatPrompt,
    selectedCameraIds,
    startTime,
    aiChatAnswerStates,
    spotCheckSchema,
    editSpotCheckSchema,
  ]);

  const livePreviewValidationError = React.useMemo(() => {
    try {
      livePreviewSchema.validateSync(
        {
          aiChatPrompt,
          selectedCameraIds,
          aiChatAnswerStates,
        },
        { abortEarly: false },
      );
    } catch (error) {
      if (error instanceof ValidationError) {
        return error;
      }
      return undefined;
    }
  }, [aiChatPrompt, selectedCameraIds, aiChatAnswerStates, livePreviewSchema]);

  const eventLivePreviewValidationError = React.useMemo(() => {
    try {
      eventLivePreviewSchema.validateSync(
        {
          title,
          aiChatPrompt,
          selectedEventReport,
          eventTimestampOffset,
          aiChatAnswerStates,
        },
        { abortEarly: false },
      );
    } catch (error) {
      if (error instanceof ValidationError) {
        return error;
      }
      return undefined;
    }
  }, [
    title,
    aiChatPrompt,
    selectedEventReport,
    eventTimestampOffset,
    aiChatAnswerStates,
    eventLivePreviewSchema,
  ]);

  const isValidSpotCheck = !spotCheckValidationError;
  const isValidPreview = !livePreviewValidationError;
  const isValidEventPreview = !eventLivePreviewValidationError;

  const addAnswerState = React.useCallback(() => {
    if (aiChatAnswerStates.length >= MAX_STATES) return;
    const newId = String(Date.now());
    setAiChatAnswerStates([...aiChatAnswerStates, { id: newId, name: '', description: '' }]);
  }, [aiChatAnswerStates]);

  const updateAnswerState = React.useCallback(
    (id: string, updates: Partial<SpotCheckState>) => {
      setAiChatAnswerStates(
        aiChatAnswerStates.map((state) => (state.id === id ? { ...state, ...updates } : state)),
      );
    },
    [aiChatAnswerStates],
  );

  const removeAnswerState = React.useCallback(
    (id: string) => {
      if (aiChatAnswerStates.length <= MIN_STATES) return; // Keep at least MIN_STATES states
      setAiChatAnswerStates(aiChatAnswerStates.filter((state) => state.id !== id));
    },
    [aiChatAnswerStates],
  );

  const spotCheckForm = React.useMemo(
    () => ({
      title,
      aiChatPrompt,
      aiChatAnswerFormat,
      startTime,
      createSpotCheck,
      setAiChatPrompt,
      setAiChatAnswerFormat,
      setStartTime,
      recurrenceOptions,
      recurrence,
      setRecurrence,
      reoccurringCount,
      setReoccurringCount,
      weeklyScheduleOption,
      setWeeklyScheduleOption,
      endTime,
      selectedDaysOfWeek,
      setSelectedDaysOfWeek,
      setEndTime,
      selectedEndType,
      setSelectedEndType,
      newActivity,
      editSpotCheck,
      setTitle,
      eventTimestampOffset,
      setEventTimestampOffset,
      eventTimeTarget,
      setEventTimeTarget,
      selectedEventReport,
      setSelectedEventReport,
      isValidSpotCheck,
      isValidPreview,
      isValidEventPreview,
      spotCheckValidationError,
      livePreviewValidationError,
      eventLivePreviewValidationError,
      fiveMinutesFromNow,
      aiChatAnswerStates,
      addAnswerState,
      updateAnswerState,
      removeAnswerState,
    }),
    [
      title,
      aiChatPrompt,
      aiChatAnswerFormat,
      startTime,
      createSpotCheck,
      recurrenceOptions,
      recurrence,
      reoccurringCount,
      weeklyScheduleOption,
      endTime,
      selectedDaysOfWeek,
      selectedEndType,
      newActivity,
      editSpotCheck,
      setTitle,
      eventTimestampOffset,
      eventTimeTarget,
      selectedEventReport,
      isValidSpotCheck,
      isValidPreview,
      isValidEventPreview,
      spotCheckValidationError,
      livePreviewValidationError,
      eventLivePreviewValidationError,
      fiveMinutesFromNow,
      aiChatAnswerStates,
      addAnswerState,
      updateAnswerState,
      removeAnswerState,
    ],
  );

  return spotCheckForm;
}

export type UseSpotCheckForm = ReturnType<typeof useSpotCheckForm>;
